import { useCallback, useMemo } from "react"

import { useTransportMutation, useTransportQueryUtils } from "@/transport"

import type { TransportOperationName } from "@/types_api/backend/transport/operations"

import { buildOrganizationTagsByName } from "../utils/tagCatalog"
import { normalizeTag } from "../utils/tagNormalization"
import { useOrganizationTags } from "./useOrganizationTags"

/**
 * Resource records embed tag names by value, so a rename leaves every cached
 * record holding the previous name until it is refetched. Detail readers hold
 * the name just as list readers do, so a mounted detail page needs the same
 * refresh as the list it was opened from.
 */
const TAGGED_RESOURCE_OPERATIONS = [
  "getAgent",
  "getMetric",
  "getMultiRunMetadata",
  "getPersona",
  "getRunMetadata",
  "getRunTemplate",
  "listAgents",
  "listAllAgents",
  "listMetrics",
  "listPersonas",
  "listSimulatedConversationsKeyset",
  "listRunTemplates",
  "listRunsKeyset",
  "listTestSets",
  "queryUploadedConversationKeyset",
  "testSetMetadata",
] as const satisfies readonly TransportOperationName[]

export function useUpdateOrganizationTagColor() {
  const { tags, invalidate } = useOrganizationTags()
  const transportQueryUtils = useTransportQueryUtils()
  const tagsByName = useMemo(() => buildOrganizationTagsByName(tags), [tags])

  // invalidateOperation rather than invalidate: these operations are cached per
  // request, and invalidate without a request only matches the request-less
  // entry, so a detail or keyset reader would keep its stale rows.
  const invalidateTaggedResources = useCallback(
    () =>
      Promise.all(
        TAGGED_RESOURCE_OPERATIONS.map((operation) =>
          transportQueryUtils.invalidateOperation(operation),
        ),
      ),
    [transportQueryUtils],
  )

  const mutation = useTransportMutation("updateOrganizationTag", {
    onSuccess: (_response, variables) => {
      void invalidate()
      if (variables.name != null) {
        void invalidateTaggedResources()
      }
    },
  })

  const updateTagColor = useCallback(
    (tagName: string, color: string) => {
      const tag = tagsByName[normalizeTag(tagName)]
      if (!tag) {
        return
      }

      mutation.mutate({
        tag_id: tag.id,
        color,
      })
    },
    [mutation, tagsByName],
  )

  const updateTagName = useCallback(
    (tagName: string, name: string) => {
      const tag = tagsByName[normalizeTag(tagName)]
      const normalizedName = normalizeTag(name)
      if (!tag || !normalizedName) {
        return
      }

      mutation.mutate({
        tag_id: tag.id,
        name: normalizedName,
      })
    },
    [mutation, tagsByName],
  )

  return {
    updateTagColor,
    updateTagName,
    isPending: mutation.isPending,
    error: mutation.error,
  }
}
